// ============================================================
// FotocheckPage — Impresión de fotochecks con QR (DNI).
// Modo individual (por DNI) y masivo (año/nivel/grado/sección).
// ============================================================

import React, { useEffect, useMemo, useState } from 'react';
import { I, UserAvatar } from 'components/ui';
import { catalogoService } from 'services/usuarioService';
import { fotocheckService } from 'services/fotocheckService';
import { matriculaService } from 'services/matriculaService';
import {
  generarFotocheckPDF,
  generarFotocheckIndividualPDF,
  OrientacionFotocheck,
} from 'utils/fotocheckPdf';
import { AñoEscolar, Grado, Seccion } from 'types/usuario.types';
import { FotocheckEstudiante } from 'types/fotocheck.types';
import { fmtAnioEscolar } from 'utils/anioEscolar';

const NIVELES = ['INICIAL', 'PRIMARIA', 'SECUNDARIA'];

const Spinner: React.FC<{ size?: number }> = ({ size = 5 }) => (
  <svg className={`animate-spin w-${size} h-${size}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z" />
  </svg>
);

type Modo = 'masivo' | 'individual';

const FotocheckPage: React.FC = () => {
  const [modo, setModo] = useState<Modo>('masivo');
  const [orientacion, setOrientacion] = useState<OrientacionFotocheck>('horizontal');

  // Catálogos
  const [anios, setAnios] = useState<AñoEscolar[]>([]);
  const [grados, setGrados] = useState<Grado[]>([]);
  const [secciones, setSecciones] = useState<Seccion[]>([]);

  // Filtros masivo
  const [anioEscolarId, setAnioEscolarId] = useState<number | ''>('');
  const [nivel, setNivel] = useState('');
  const [gradoId, setGradoId] = useState<number | ''>('');
  const [seccionId, setSeccionId] = useState<number | ''>('');

  // Individual
  const [dniBusqueda, setDniBusqueda] = useState('');

  // Resultados
  const [estudiantes, setEstudiantes] = useState<FotocheckEstudiante[]>([]);
  const [seleccionados, setSeleccionados] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generando, setGenerando] = useState(false);

  // ── Cargar catálogos al montar ──────────────────────────────
  useEffect(() => {
    Promise.all([
      catalogoService.getAñosEscolares(),
      catalogoService.getGrados(),
      catalogoService.getSecciones(),
    ]).then(([a, g, s]) => {
      setAnios(a);
      setGrados(g);
      setSecciones(s);
      const activo = a.find(x => x.activo) ?? a[0];
      if (activo) setAnioEscolarId(activo.idAnioEscolar);
    }).catch(err => {
      setError(err instanceof Error ? err.message : 'Error al cargar catálogos');
    });
  }, []);

  // ── Cascada nivel/grado/sección ─────────────────────────────
  const gradosFiltrados = useMemo(
    () => nivel ? grados.filter(g => g.nivel === nivel) : grados,
    [grados, nivel],
  );
  const seccionesFiltradas = useMemo(
    () => gradoId ? secciones.filter(s => s.gradoId === gradoId) : [],
    [secciones, gradoId],
  );

  useEffect(() => { setGradoId(''); setSeccionId(''); }, [nivel]);
  useEffect(() => { setSeccionId(''); }, [gradoId]);

  // ── Buscar estudiantes ──────────────────────────────────────
  const buscarMasivo = async () => {
    if (!anioEscolarId) { setError('Selecciona un año escolar'); return; }
    setLoading(true); setError(null); setEstudiantes([]); setSeleccionados(new Set());
    try {
      const data = await fotocheckService.listar({
        anioEscolarId: Number(anioEscolarId),
        nivel:     nivel || undefined,
        gradoId:   gradoId   ? Number(gradoId)   : undefined,
        seccionId: seccionId ? Number(seccionId) : undefined,
      });
      setEstudiantes(data);
      setSeleccionados(new Set(data.map(e => e.matriculaId)));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al buscar estudiantes');
    } finally {
      setLoading(false);
    }
  };

  const buscarIndividual = async () => {
    const dni = dniBusqueda.trim();
    if (!/^\d{8}$/.test(dni)) { setError('Ingresa un DNI válido de 8 dígitos'); return; }
    if (!anioEscolarId) { setError('Selecciona un año escolar'); return; }
    setLoading(true); setError(null); setEstudiantes([]); setSeleccionados(new Set());
    try {
      const matricula = await matriculaService.buscarPorDni(dni, Number(anioEscolarId));
      if (!matricula) {
        setError('No se encontró una matrícula activa con ese DNI');
        return;
      }
      const data = await fotocheckService.listar({
        anioEscolarId: Number(anioEscolarId),
        estudianteId: matricula.estudianteId,
      });
      setEstudiantes(data);
      setSeleccionados(new Set(data.map(e => e.matriculaId)));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al buscar estudiante');
    } finally {
      setLoading(false);
    }
  };

  // ── Selección ───────────────────────────────────────────────
  const toggleSel = (id: number) => {
    setSeleccionados(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const togglarTodos = () => {
    if (seleccionados.size === estudiantes.length) setSeleccionados(new Set());
    else setSeleccionados(new Set(estudiantes.map(e => e.matriculaId)));
  };

  // ── Generar PDF ─────────────────────────────────────────────
  const seleccionList = estudiantes.filter(e => seleccionados.has(e.matriculaId));

  const imprimirIndividual = async (est: FotocheckEstudiante) => {
    setGenerando(true);
    try {
      await generarFotocheckIndividualPDF(est, orientacion);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al generar el PDF');
    } finally {
      setGenerando(false);
    }
  };

  const imprimirMasivo = async () => {
    if (seleccionList.length === 0) return;
    setGenerando(true);
    try {
      const sufijo = nivel || 'todos';
      await generarFotocheckPDF(seleccionList, `fotochecks-${sufijo}-${anioEscolarId}-${orientacion}`, orientacion);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al generar el PDF');
    } finally {
      setGenerando(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-800">Fotochecks Estudiantiles</h2>
        <p className="text-slate-400 text-sm mt-0.5">
          Imprime credenciales con código QR del DNI para asistencia diaria
        </p>
      </div>

      {/* Controles superiores: modo + orientación */}
      <div className="flex flex-wrap items-start gap-4">
        {/* Selector de modo */}
        <div>
          <p className="text-[11px] font-bold text-slate-500 uppercase mb-1">Modo</p>
          <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
            {([
              { key: 'masivo',     label: 'Masivo (por filtros)' },
              { key: 'individual', label: 'Individual (por DNI)' },
            ] as const).map(t => (
              <button
                key={t.key}
                onClick={() => { setModo(t.key); setEstudiantes([]); setError(null); }}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all
                  ${modo === t.key ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Toggle de orientación */}
        <div>
          <p className="text-[11px] font-bold text-slate-500 uppercase mb-1">Orientación</p>
          <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
            <button
              onClick={() => setOrientacion('horizontal')}
              title="Horizontal — 86 × 54 mm — 10 por A4"
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all
                ${orientacion === 'horizontal' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <span className="inline-block w-5 h-3 rounded-sm border-2 border-current" />
              Horizontal
            </button>
            <button
              onClick={() => setOrientacion('vertical')}
              title="Vertical — 54 × 86 mm — 9 por A4"
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all
                ${orientacion === 'vertical' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <span className="inline-block w-3 h-5 rounded-sm border-2 border-current" />
              Vertical
            </button>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        {modo === 'masivo' ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Año escolar *</label>
              <select value={anioEscolarId} onChange={e => setAnioEscolarId(e.target.value ? Number(e.target.value) : '')}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
                <option value="">Selecciona…</option>
                {anios.map(a => <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Nivel</label>
              <select value={nivel} onChange={e => setNivel(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
                <option value="">Todos</option>
                {NIVELES.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Grado</label>
              <select value={gradoId} onChange={e => setGradoId(e.target.value ? Number(e.target.value) : '')}
                disabled={gradosFiltrados.length === 0}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:opacity-50">
                <option value="">Todos</option>
                {gradosFiltrados.map(g => <option key={g.idGrado} value={g.idGrado}>{g.nombre} ({g.nivel})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Sección</label>
              <select value={seccionId} onChange={e => setSeccionId(e.target.value ? Number(e.target.value) : '')}
                disabled={seccionesFiltradas.length === 0}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:opacity-50">
                <option value="">Todas</option>
                {seccionesFiltradas.map(s => <option key={s.idSeccion} value={s.idSeccion}>{s.nombre}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={buscarMasivo}
                disabled={loading || !anioEscolarId}
                className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold disabled:opacity-60"
              >
                {loading ? <Spinner /> : <I.Search size={14} />} Buscar
              </button>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Año escolar *</label>
              <select value={anioEscolarId} onChange={e => setAnioEscolarId(e.target.value ? Number(e.target.value) : '')}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
                <option value="">Selecciona…</option>
                {anios.map(a => <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">DNI del estudiante *</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={8}
                value={dniBusqueda}
                onChange={e => setDniBusqueda(e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => e.key === 'Enter' && buscarIndividual()}
                placeholder="00000000"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none tabular-nums tracking-widest"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={buscarIndividual}
                disabled={loading || !anioEscolarId || dniBusqueda.length !== 8}
                className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold disabled:opacity-60"
              >
                {loading ? <Spinner /> : <I.Search size={14} />} Buscar
              </button>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600 text-sm">{error}</div>
      )}

      {/* Resultados */}
      {estudiantes.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={seleccionados.size === estudiantes.length}
                  onChange={togglarTodos}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                Seleccionar todos
              </label>
              <span className="text-sm text-slate-400">
                {seleccionados.size} de {estudiantes.length} seleccionados
              </span>
            </div>
            <button
              onClick={imprimirMasivo}
              disabled={generando || seleccionList.length === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-60"
            >
              {generando ? <Spinner /> : <I.Printer size={14} />}
              Imprimir {seleccionList.length} fotocheck{seleccionList.length !== 1 ? 's' : ''}
            </button>
          </div>

          {/* Lista en grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
            {estudiantes.map(est => (
              <CardEstudiante
                key={est.matriculaId}
                est={est}
                seleccionado={seleccionados.has(est.matriculaId)}
                onToggle={() => toggleSel(est.matriculaId)}
                onImprimir={() => imprimirIndividual(est)}
              />
            ))}
          </div>
        </div>
      )}

      {!loading && estudiantes.length === 0 && !error && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-12 text-center">
          <p className="text-slate-400 text-sm">
            {modo === 'masivo'
              ? 'Aplica los filtros y pulsa Buscar para listar estudiantes'
              : 'Ingresa el DNI del estudiante y pulsa Buscar'}
          </p>
        </div>
      )}
    </div>
  );
};

// ── Card por estudiante ───────────────────────────────────────

const NIVEL_COLOR: Record<string, string> = {
  INICIAL:    'bg-amber-100 text-amber-700',
  PRIMARIA:   'bg-blue-100 text-blue-700',
  SECUNDARIA: 'bg-purple-100 text-purple-700',
};

const CardEstudiante: React.FC<{
  est: FotocheckEstudiante;
  seleccionado: boolean;
  onToggle: () => void;
  onImprimir: () => void;
}> = ({ est, seleccionado, onToggle, onImprimir }) => (
  <div className={`relative rounded-xl border p-3 flex items-center gap-3 transition-all
    ${seleccionado ? 'border-blue-300 bg-blue-50/40 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
    <input
      type="checkbox"
      checked={seleccionado}
      onChange={onToggle}
      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
    />
    <UserAvatar
      idUsuario={est.estudianteId}
      fotoPerfil={est.fotoPerfil}
      nombres={est.estudianteNombres}
      apellidos={est.estudianteApellidos}
      size={40}
      rounded="xl"
    />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-bold text-slate-800 truncate">
        {est.estudianteApellidos}, {est.estudianteNombres}
      </p>
      <p className="text-xs text-slate-400 mt-0.5">
        DNI {est.estudianteDni} · {est.gradoNombre} {est.seccionNombre}
      </p>
      <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${NIVEL_COLOR[est.nivel] ?? 'bg-slate-100 text-slate-600'}`}>
        {est.nivel}
      </span>
    </div>
    <button
      onClick={onImprimir}
      title="Imprimir individual"
      className="p-2 rounded-lg bg-slate-100 hover:bg-blue-100 text-slate-600 hover:text-blue-700 flex-shrink-0"
    >
      <I.Printer size={14} />
    </button>
  </div>
);

export default FotocheckPage;
